import type { SimCareerView, SimMe } from '@cruz/shared';
import { useState, type FormEvent } from 'react';
import { CrossMark } from '../components/icons.tsx';
import { BrothersSection } from './Brothers.tsx';
import { Portrait } from './Cast.tsx';
import { NextStepCard } from './NextStep.tsx';
import type { ThemeMode } from '../hooks/useTheme.ts';
import { simApi, simErrorMessage } from './api.ts';
import { brotherhoodName, euros, num, plural, relationLabel, upperFirst } from './format.ts';
import { ShareButton } from './Share.tsx';
import {
  card,
  inputClass,
  Link,
  linkClass,
  Meter,
  Notice,
  PageTitle,
  primaryButton,
  secondaryButton,
} from './ui.tsx';

/** The other sections of the game: brotherhood, my life, characters, achievements, account. */

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="text-ink-soft dark:text-cream/75">{label}</dt>
      <dd className="tabular text-right font-semibold">{value}</dd>
    </div>
  );
}

export function BrotherhoodTab({ career }: { career: SimCareerView }) {
  const b = career.brotherhood;
  const p = career.player;
  return (
    <div className="space-y-4">
      <PageTitle kicker="Tu hermandad">{upperFirst(brotherhoodName(b))}</PageTitle>
      <p className="text-ink-soft dark:text-cream/80">
        {b.name}. Sale el {b.day} desde {b.church}.
      </p>
      <dl className={`${card} divide-y divide-purple/10 px-4 dark:divide-cream/10`}>
        <Row label="Hermanos" value={num(b.members)} />
        <Row label="Nazarenos en la cofradía" value={num(b.nazarenes)} />
        <Row label="Prestigio en Sevilla" value={`${b.prestige} de 100`} />
        <Row label="Caja de la mayordomía" value={euros(b.treasury)} />
      </dl>
      <h2 className="font-serif text-2xl font-semibold text-purple dark:text-gold-300">Tú</h2>
      <dl className={`${card} divide-y divide-purple/10 px-4 dark:divide-cream/10`}>
        <Row label="Cargo" value={`${p.rank_label}${p.post ? ` · ${p.post}` : ''}`} />
        <Row label="Número de hermano" value={num(p.member_number)} />
        <Row label="Antigüedad" value={plural(p.seniority_years, 'año', 'años')} />
        <Row label="Puesto en la nómina" value={num(p.position)} />
        {p.tramo !== null && <Row label="Tramo" value={`${p.tramo} de ${p.tramos}`} />}
        <Row label="Estaciones de penitencia" value={num(p.processions)} />
        {career.time.mandate_left !== null && (
          <Row
            label="Mandato"
            value={`Quedan ${plural(career.time.mandate_left, 'año', 'años')}`}
          />
        )}
      </dl>
      <BrothersSection brotherhood={b} />
      {b.milestones.length > 0 && (
        <>
          <h2 className="font-serif text-2xl font-semibold text-purple dark:text-gold-300">
            Hitos
          </h2>
          <ul className="flex flex-wrap gap-2">
            {b.milestones.map((m) => (
              <li
                key={m.key}
                className="rounded-full border border-gold/70 px-3 py-1 text-sm font-medium"
              >
                {m.label}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function LifeTab({ career }: { career: SimCareerView }) {
  const byYear = new Map<number, SimCareerView['timeline']>();
  for (const e of [...career.timeline].reverse()) {
    byYear.set(e.year, [...(byYear.get(e.year) ?? []), e]);
  }
  return (
    <div className="space-y-5">
      <PageTitle kicker="Mi vida cofrade">Tus momentos</PageTitle>
      {career.player.next_step && <NextStepCard step={career.player.next_step} />}
      {[...byYear].map(([year, entries]) => (
        <section key={year}>
          <h2 className="mb-2 text-sm font-semibold tracking-[0.12em] text-gold-700 uppercase dark:text-gold-300">
            Año {year}
          </h2>
          <ol className="space-y-2 border-l-2 border-gold/60 pl-4">
            {entries.map((e) => (
              <li key={e.index} className="relative">
                <span
                  className="absolute top-2 -left-[1.42rem] h-2.5 w-2.5 rotate-45 bg-gold"
                  aria-hidden
                />
                <p className="text-[1.02rem]">{e.text}</p>
                <p className="text-xs text-ink-soft dark:text-cream/60">{e.phase_label}</p>
                {e.shareable && (
                  <div className="mt-1.5">
                    <ShareButton request={{ kind: 'moment', ref: String(e.index) }} />
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

export function CharactersTab({ career }: { career: SimCareerView }) {
  const people = [...career.characters].sort((a, z) => z.relation - a.relation);
  return (
    <div className="space-y-4">
      <PageTitle kicker="Personajes">La gente de tu hermandad</PageTitle>
      <ul className="space-y-2">
        {people.map((c) => (
          <li key={c.id} className={`${card} px-4 py-3`}>
            <div className="flex items-center gap-3">
              <Portrait id={c.id} name={c.name} src={c.portrait} size={56} />
              <div className="flex min-w-0 flex-1 flex-wrap items-baseline justify-between gap-x-3">
                <p className="text-lg font-semibold">{c.name}</p>
                <p className="text-sm font-medium text-gold-700 dark:text-gold-300">
                  {relationLabel(c.relation)}
                </p>
              </div>
            </div>
            {c.thread && (
              <p
                className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-sm font-medium ${
                  c.thread === 'favor'
                    ? 'bg-gold/15 text-gold-700 dark:text-gold-300'
                    : 'bg-burgundy-300/25 text-burgundy dark:bg-burgundy/25 dark:text-burgundy-300'
                }`}
              >
                {c.thread === 'favor' ? 'Te debe una' : 'Hay algo pendiente'}
              </p>
            )}
            <p className="mb-2 text-ink-soft dark:text-cream/75">{upperFirst(c.description)}</p>
            <Meter value={(c.relation + 10) * 5} label={`Relación con ${c.name}`} />
          </li>
        ))}
      </ul>
    </div>
  );
}

const LockIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    aria-hidden
  >
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 018 0v3" />
  </svg>
);

/**
 * Logros: the ones you have, as holy cards (estampitas) to share, and the ones still to
 * get, with what it takes, so there is always a next one in sight.
 */
const LOCKED_SHOWN = 6;

export function AchievementsTab({ career }: { career: SimCareerView }) {
  const [allLocked, setAllLocked] = useState(false);
  const unlocked = career.achievements.filter((a) => a.unlocked);
  const locked = career.achievements.filter((a) => !a.unlocked);
  const total = career.achievements.length;
  return (
    <div className="space-y-5">
      <PageTitle kicker="Logros">Tus estampitas</PageTitle>
      <div>
        <p className="mb-1.5 flex items-baseline justify-between text-ink-soft dark:text-cream/80">
          <span>
            Llevas <strong className="text-ink dark:text-cream">{unlocked.length}</strong> de{' '}
            {total}.
          </span>
          <span className="text-sm">
            {Math.round((unlocked.length / Math.max(1, total)) * 100)} %
          </span>
        </p>
        <Meter value={(unlocked.length / Math.max(1, total)) * 100} label="Logros conseguidos" />
      </div>

      {unlocked.length > 0 ? (
        <ul aria-label="Conseguidos" className="grid grid-cols-2 gap-3">
          {unlocked.map((a) => (
            <li
              key={a.id}
              className="animate-rise relative flex flex-col overflow-hidden rounded-2xl border-2 border-gold bg-paper px-3 pt-3 pb-3 text-center shadow-[0_2px_10px_rgba(118,90,42,0.12)] dark:bg-night-800"
            >
              {/* An inner gold rule, like the border of a holy card */}
              <span
                className="pointer-events-none absolute inset-1.5 rounded-xl border border-gold/40"
                aria-hidden
              />
              <span className="mx-auto text-gold-700 dark:text-gold-300">
                <CrossMark size={22} />
              </span>
              <p className="mt-1 font-serif text-lg leading-tight font-semibold text-purple dark:text-cream">
                {a.name}
              </p>
              <p className="mt-1 flex-1 text-sm leading-snug text-ink-soft dark:text-cream/80">
                {a.description}
              </p>
              {a.year !== null && (
                <p className="mt-1.5 text-xs font-semibold tracking-[0.14em] text-gold-700 uppercase dark:text-gold-300">
                  Año {a.year}
                </p>
              )}
              {a.shareable && (
                <div className="relative mt-2">
                  <ShareButton request={{ kind: 'achievement', ref: a.id }} />
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <Notice>Tu primera estampita llega pronto: sigue jugando cada día.</Notice>
      )}

      {locked.length > 0 && (
        <section aria-labelledby="locked-title">
          <h2
            id="locked-title"
            className="mb-2 font-serif text-xl font-semibold text-purple dark:text-gold-300"
          >
            Por conseguir
          </h2>
          <ul className={`${card} divide-y divide-purple/10 dark:divide-cream/10`}>
            {(allLocked ? locked : locked.slice(0, LOCKED_SHOWN)).map((a) => (
              <li key={a.id} className="flex items-start gap-3 px-4 py-3">
                <span className="mt-0.5 text-gold-700/70 dark:text-gold-300/60">
                  <LockIcon />
                </span>
                <div className="min-w-0">
                  <p className="font-medium">{a.name}</p>
                  <p className="text-sm leading-snug text-ink-soft dark:text-cream/70">
                    {a.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          {!allLocked && locked.length > LOCKED_SHOWN && (
            <button
              type="button"
              className={`${secondaryButton} mt-3`}
              onClick={() => setAllLocked(true)}
            >
              Ver los {locked.length - LOCKED_SHOWN} que faltan
            </button>
          )}
        </section>
      )}
    </div>
  );
}

const THEME_NAMES: Record<ThemeMode, string> = {
  auto: 'Automático (como el móvil)',
  light: 'Claro',
  dark: 'Oscuro',
};

export function SettingsTab({
  me,
  theme,
  onTheme,
  onMe,
  onLogout,
  onDeleted,
}: {
  me: SimMe;
  theme: ThemeMode;
  onTheme: () => void;
  onMe: (me: SimMe) => void;
  onLogout: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [password, setPassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (me.guest) return <GuestSettings theme={theme} onTheme={onTheme} onDeleted={onDeleted} />;

  const toggleMarketing = async () => {
    setBusy(true);
    setMessage(null);
    try {
      onMe(await simApi.preferences(!me.marketing));
    } catch (err) {
      setMessage(simErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const logout = async () => {
    try {
      await simApi.logout();
    } finally {
      onLogout();
    }
  };
  const remove = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setDeleteError(null);
    try {
      await simApi.deleteAccount(password);
      onDeleted();
    } catch (err) {
      setDeleteError(simErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Without a career (or before the first event) there is no tab bar: the way back */}
      <Link to="/simulador" className={`${linkClass} inline-flex min-h-11 items-center`}>
        ← Volver al juego
      </Link>
      <PageTitle kicker="Ajustes">Tu cuenta</PageTitle>
      <dl className={`${card} divide-y divide-purple/10 px-4 dark:divide-cream/10`}>
        <Row label="Apodo" value={me.nickname ?? ''} />
        <Row label="Correo" value={me.email ?? ''} />
        <Row label="Correo verificado" value={me.email_verified ? 'Sí' : 'Todavía no'} />
      </dl>
      {!me.email_verified && (
        <button
          type="button"
          className={secondaryButton}
          onClick={() => {
            simApi
              .resendVerification()
              .then(() => setMessage('Te hemos mandado el enlace. Mira tu correo.'))
              .catch((err: unknown) => setMessage(simErrorMessage(err)));
          }}
        >
          Mandarme el enlace de verificación
        </button>
      )}
      <section className={`${card} px-4 py-4`}>
        <h2 className="font-serif text-xl font-semibold text-purple dark:text-gold-300">
          Correos con novedades
        </h2>
        <p className="mt-1 text-ink-soft dark:text-cream/80">
          {me.marketing
            ? 'Te mandamos novedades del juego, de Cruz de IA y del proyecto.'
            : 'No te mandamos novedades. Solo los correos de tu cuenta (verificación, contraseña).'}
        </p>
        <button
          type="button"
          className={`${secondaryButton} mt-3`}
          onClick={() => void toggleMarketing()}
          disabled={busy}
        >
          {me.marketing ? 'No quiero más novedades' : 'Quiero recibir novedades'}
        </button>
      </section>
      <section className={`${card} flex items-center justify-between gap-3 px-4 py-3`}>
        <div>
          <h2 className="font-serif text-xl font-semibold text-purple dark:text-gold-300">Tema</h2>
          <p className="text-ink-soft dark:text-cream/80">{THEME_NAMES[theme]}</p>
        </div>
        <button
          type="button"
          className="min-h-11 rounded-full border border-purple/25 px-4 text-sm font-semibold text-purple dark:border-cream/20 dark:text-cream"
          onClick={onTheme}
        >
          Cambiar
        </button>
      </section>
      {message && <Notice>{message}</Notice>}
      <button type="button" className={secondaryButton} onClick={() => void logout()}>
        Cerrar sesión
      </button>
      <section className="rounded-2xl border border-burgundy/40 px-4 py-4 dark:border-burgundy-300/40">
        <h2 className="font-serif text-xl font-semibold text-burgundy dark:text-burgundy-300">
          Borrar mi cuenta
        </h2>
        <p className="mt-1 text-ink-soft dark:text-cream/80">
          Se borran tu cuenta, tus partidas y tus tarjetas compartidas. No se puede deshacer.
        </p>
        {!deleting ? (
          <button
            type="button"
            className={`${secondaryButton} mt-3`}
            onClick={() => setDeleting(true)}
          >
            Quiero borrar mi cuenta
          </button>
        ) : (
          <form className="mt-3 space-y-3" onSubmit={(e) => void remove(e)}>
            <label htmlFor="delete-password" className="block font-semibold">
              Escribe tu contraseña para confirmar
            </label>
            <input
              id="delete-password"
              type="password"
              autoComplete="current-password"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {deleteError && <Notice tone="error">{deleteError}</Notice>}
            <button
              type="submit"
              disabled={busy}
              className={`${primaryButton} !bg-burgundy hover:!bg-burgundy/90 dark:!bg-burgundy-300 dark:!text-night`}
            >
              Borrar mi cuenta para siempre
            </button>
          </form>
        )}
      </section>
      <p className="text-center text-sm">
        <Link to="/simulador/condiciones">Condiciones</Link>
        {' · '}
        <Link to="/simulador/privacidad">Privacidad</Link>
      </p>
    </div>
  );
}

/** Settings of a guest: save the career (or take it to an account), the theme, start over. */
function GuestSettings({
  theme,
  onTheme,
  onDeleted,
}: {
  theme: ThemeMode;
  onTheme: () => void;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startOver = async () => {
    try {
      await simApi.deleteAccount('');
      onDeleted();
    } catch (err) {
      setError(simErrorMessage(err));
    }
  };
  return (
    <div className="space-y-5">
      <Link to="/simulador" className={`${linkClass} inline-flex min-h-11 items-center`}>
        ← Volver al juego
      </Link>
      <PageTitle kicker="Ajustes">Juegas sin cuenta</PageTitle>
      <section className={`${card} space-y-3 px-4 py-4`}>
        <p>
          Tu carrera solo está en este móvil. Si borras los datos del navegador o cambias de móvil,
          la pierdes. Guárdala con una cuenta y sigue desde cualquier sitio.
        </p>
        <Link to="/simulador/registro" className={primaryButton}>
          Guardar mi carrera
        </Link>
        <p className="text-center">
          ¿Ya tenías cuenta? <Link to="/simulador/entrar">Entra</Link>
        </p>
      </section>
      <section className={`${card} flex items-center justify-between gap-3 px-4 py-3`}>
        <div>
          <h2 className="font-serif text-xl font-semibold text-purple dark:text-gold-300">Tema</h2>
          <p className="text-ink-soft dark:text-cream/80">{THEME_NAMES[theme]}</p>
        </div>
        <button
          type="button"
          className="min-h-11 rounded-full border border-purple/25 px-4 text-sm font-semibold text-purple dark:border-cream/20 dark:text-cream"
          onClick={onTheme}
        >
          Cambiar
        </button>
      </section>
      <section className="rounded-2xl border border-burgundy/40 px-4 py-4 dark:border-burgundy-300/40">
        <h2 className="font-serif text-xl font-semibold text-burgundy dark:text-burgundy-300">
          Empezar de cero
        </h2>
        <p className="mt-1 text-ink-soft dark:text-cream/80">
          Se borra esta partida para siempre. Si no vuelves a jugar, se borra sola en un mes.
        </p>
        {confirming ? (
          <button
            type="button"
            className={`${primaryButton} mt-3 !bg-burgundy hover:!bg-burgundy/90 dark:!bg-burgundy-300 dark:!text-night`}
            onClick={() => void startOver()}
          >
            Sí, borrar esta partida
          </button>
        ) : (
          <button
            type="button"
            className={`${secondaryButton} mt-3`}
            onClick={() => setConfirming(true)}
          >
            Borrar esta partida
          </button>
        )}
        {error && <Notice tone="error">{error}</Notice>}
      </section>
      <p className="text-center text-sm">
        <Link to="/simulador/condiciones">Condiciones</Link>
        {' · '}
        <Link to="/simulador/privacidad">Privacidad</Link>
      </p>
    </div>
  );
}
